import type { Camp } from '@orbomb/sim';

export type RoomStatus = 'waiting' | 'playing' | 'finished' | 'closed';

export interface OnlineRoom {
  id: string;
  code: string;
  hostId: string;
  guestId: string | null;
  hostName: string;
  guestName: string | null;
  status: RoomStatus;
  seed: number;
  mapId: string;
  /** Seat held by a name-only visitor. A match with one is never ranked. */
  hostVisitor: boolean;
  guestVisitor: boolean;
}

export interface LadderEntry {
  id: string;
  displayName: string;
  wins: number;
  losses: number;
  pointsFor: number;
  pointsAgainst: number;
}

export interface Account {
  id: string;
  email: string;
  displayName: string;
  wins: number;
  losses: number;
}

const STATUSES: readonly RoomStatus[] = ['waiting', 'playing', 'finished', 'closed'];

function row(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function whole(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  if (typeof value === 'string' && /^(0|[1-9]\d*)$/.test(value)) return Number(value);
  return null;
}

export function parseRoom(value: unknown): OnlineRoom | null {
  const source = Array.isArray(value) ? value[0] : value;
  const raw = row(source);
  if (!raw) return null;
  const id = text(raw.id);
  const code = text(raw.code);
  const hostId = text(raw.host_id);
  const hostName = text(raw.host_name);
  const status = STATUSES.find((item) => item === raw.status);
  const seed = whole(raw.seed);
  const mapId = text(raw.map_id);
  if (!id || !code || !hostId || !hostName || !status || seed === null || !mapId) return null;
  const guestId = raw.guest_id === null ? null : text(raw.guest_id);
  const guestName =
    raw.guest_name === null || raw.guest_name === undefined ? null : text(raw.guest_name);
  if (raw.guest_id !== null && raw.guest_id !== undefined && !guestId) return null;
  return {
    id,
    code,
    hostId,
    guestId,
    hostName,
    guestName,
    status,
    seed,
    mapId,
    hostVisitor: raw.host_visitor === true,
    guestVisitor: raw.guest_visitor === true,
  };
}

export function parseRoomList(value: unknown): OnlineRoom[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const room = parseRoom(item);
    return room ? [room] : [];
  });
}

export function parseLadder(value: unknown): LadderEntry[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const raw = row(item);
    if (!raw) return [];
    const id = text(raw.id);
    const displayName = text(raw.display_name);
    const wins = whole(raw.wins);
    const losses = whole(raw.losses);
    const pointsFor = whole(raw.points_for);
    const pointsAgainst = whole(raw.points_against);
    if (
      !id ||
      !displayName ||
      wins === null ||
      losses === null ||
      pointsFor === null ||
      pointsAgainst === null
    ) {
      return [];
    }
    return [{ id, displayName, wins, losses, pointsFor, pointsAgainst }];
  });
}

export function parseProfile(id: string, email: string, value: unknown): Account {
  const raw = row(value);
  return {
    id,
    email,
    displayName: text(raw?.display_name) ?? 'Player',
    wins: whole(raw?.wins) ?? 0,
    losses: whole(raw?.losses) ?? 0,
  };
}

export interface MyMatch {
  roomId: string;
  won: boolean;
  opponent: string;
  /** Remaining lives for the signed-in player, then the opponent. */
  mine: number;
  theirs: number;
}

/** Match rows the signed-in player played, newest first as the query returned them. */
export function parseMyMatches(userId: string, value: unknown): MyMatch[] {
  if (!Array.isArray(value)) return [];
  const matches: MyMatch[] = [];
  for (const item of value) {
    const raw = row(item);
    if (!raw) continue;
    const roomId = text(raw.room_id);
    const hostId = text(raw.host_id);
    const guestId = text(raw.guest_id);
    const winnerId = text(raw.winner_id);
    const orange = whole(raw.orange_score);
    const turquoise = whole(raw.turquoise_score);
    if (!roomId || !hostId || !guestId || !winnerId || orange === null || turquoise === null) {
      continue;
    }
    const asHost = hostId === userId;
    if (!asHost && guestId !== userId) continue;
    const room = row(Array.isArray(raw.rooms) ? raw.rooms[0] : raw.rooms);
    const hostName = text(room?.host_name) ?? 'Host';
    const guestName = text(room?.guest_name) ?? 'Guest';
    matches.push({
      roomId,
      won: winnerId === userId,
      opponent: asHost ? guestName : hostName,
      mine: asHost ? turquoise : orange,
      theirs: asHost ? orange : turquoise,
    });
  }
  return matches;
}

/** Keep the signed-in W–L in step with the ladder row for that account. */
export function accountFromLadder(account: Account, ladder: readonly LadderEntry[]): Account {
  const mine = ladder.find((entry) => entry.id === account.id);
  if (!mine || (mine.wins === account.wins && mine.losses === account.losses)) return account;
  return { ...account, wins: mine.wins, losses: mine.losses };
}

/** Host sits at the bottom of the unrotated arena. The guest is ORANGE and sees a rotated view. */
export function campFor(room: OnlineRoom, userId: string): Camp | null {
  if (room.hostId === userId) return 'TURQUOISE';
  if (room.guestId === userId) return 'ORANGE';
  return null;
}

/** A player name as the lobby shows it: visitors carry the guest mark. */
export function seatName(name: string, visitor: boolean): string {
  return visitor ? `${name} (guest)` : name;
}

/** Match-over line when a visitor held a seat, or null for a ranked room. */
export function unrankedNote(room: OnlineRoom, camp: Camp): string | null {
  if (!room.hostVisitor && !room.guestVisitor) return null;
  const mine = camp === 'TURQUOISE' ? room.hostVisitor : room.guestVisitor;
  return mine
    ? 'Guest match: scores are not saved. Sign in to keep yours.'
    : 'Unranked: your opponent played as a guest.';
}
